import { api } from '@/config/api';

export type AiProviderName = 'openai' | 'claude' | 'qwen' | 'gemini' | 'mistral';
export type CampaignObjective =
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_TRAFFIC'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_APP_PROMOTION'
  | 'OUTCOME_SALES';

// ─── Generate payload ─────────────────────────────────────────────────────────

export interface GenerateCampaignPayload {
  brief: string;
  objective?: CampaignObjective;
  budget_at?: 'campaign' | 'adset';
  daily_budget_amount?: number;
  lifetime_budget_amount?: number;
  schedule?: { start?: string; stop?: string };
  countries?: string[];
  age_range?: { min: number; max: number };
  genders?: number[];
  interests_query?: string;
  cta_type?: string;
  destination_url?: string;
  page_id?: string;
  instagram_actor_id?: string;
  num_adsets?: number;
  num_ads_per_adset?: number;
  ai_provider?: AiProviderName;
  ai_model?: string;
}

// ─── Draft shapes ─────────────────────────────────────────────────────────────

export interface DraftCreative {
  creative_pending: true;
  media_kind: 'image' | 'video' | 'carousel';
  headline?: string;
  description?: string;
  message?: string;
  cta_type?: string;
  destination_url?: string;
}

export interface DraftAd {
  name: string;
  status: 'PAUSED';
  creative: DraftCreative;
}

export interface DraftTargeting {
  geo_locations?: { countries?: string[]; cities?: Array<{ key: string }> };
  age_min?: number;
  age_max?: number;
  genders?: number[];
  interests?: Array<{ id: string; name: string }>;
  custom_audiences?: string[];
  excluded_custom_audiences?: string[];
}

export interface DraftAdSet {
  name: string;
  status: 'PAUSED';
  billing_event: string;
  optimization_goal: string;
  daily_budget?: number;
  lifetime_budget?: number;
  start_time?: string;
  end_time?: string;
  targeting: DraftTargeting;
  promoted_object?: Record<string, string>;
  destination_type?: string;
  ads: DraftAd[];
}

export interface DraftCampaign {
  name: string;
  objective: CampaignObjective;
  status: 'PAUSED';
  budget_level: 'campaign' | 'adset';
  daily_budget?: number;
  lifetime_budget?: number;
  start_time?: string;
  stop_time?: string;
  special_ad_categories?: string[];
  aiGenerated?: true;
  _pageId?: string;
  _instagramActorId?: string;
}

export interface WorkspaceDraft {
  campaign: DraftCampaign;
  adsets: DraftAdSet[];
}

// ─── Response shapes ──────────────────────────────────────────────────────────

export interface GenerateCampaignResponse {
  draft: WorkspaceDraft;
  reasoning: string;
  creditsCharged?: number;
}

export interface PublishDraftResponse {
  campaign_id: string;
  adset_ids: string[];
  ad_ids: string[];
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const aiCampaignService = {
  generate: async (payload: GenerateCampaignPayload): Promise<GenerateCampaignResponse> =>
    (await api.post('/slighter_crm/facebook/ai-campaign/generate', payload)).data,

  publishDraft: async (draft: WorkspaceDraft): Promise<PublishDraftResponse> =>
    (await api.post('/slighter_crm/facebook/ai-campaign/publish-draft', draft)).data,

  enhanceAdCopy: async (payload: EnhanceFbAdCopyPayload): Promise<EnhancedFbAdCopy> =>
    (await api.post('/slighter_crm/facebook/ai-campaign/enhance-ad-copy', payload)).data,
};

// ─── Ad-copy enhance ──────────────────────────────────────────────────────────

export interface EnhanceFbAdCopyPayload {
  objective?: string;
  draftName?: string;
  draftHeadline?: string;
  draftDescription?: string;
  draftMessage?: string;
  brandName?: string;
  productOrService?: string;
  landingPageUrl?: string;
  language?: string;
  ai_provider?: AiProviderName;
  ai_model?: string;
}

export interface EnhancedFbAdCopy {
  name: string;
  headline: string;
  description: string;
  message: string;
}
